import { defineComponent as _defineComponent } from 'vue'
import AddressSearch from "@/components/address/AddressSearch.vue";
import BusStopStreetView from "@/components/map/BusStopStreetView.vue";
import MapLineTypeFilter from "@/components/map/MapLineTypeFilter.vue";
import MapZoomLevelSetter from "@/components/map/MapZoomLevelSetter.vue";
import NaverMap from "@/components/map/NaverMap.vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import Page from "@/components/shared/Page.vue";
import { IAddressSearchInfo, ICoordinateInfo } from "@/domain/entities/address";
import { IBusStopData } from "@/domain/entities/busStop";
import { ILineInfoLite } from "@/domain/entities/lineInfo";
import router from "@/router";
import { useLineStore } from "@/stores/lineStore";
import {
  BUS_STOP_MARKERS_WINDOW_SIZE_METER,
  GetCurrentLocation,
  ILineTypeCheckedMap,
  Location,
  PROVIDE_KEY_CURRENT_LOCATION,
  PROVIDE_KEY_GET_CURRENT_LOCATION,
  PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  RemoveLoadingImage,
  RouterName,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import calculateDistance from "@/use/useCalculateDistance";
import _ from "lodash";
import SwiperCore, { EffectCoverflow, Navigation, Pagination } from "swiper";
import "swiper/swiper.scss";
import { Ref } from "vue";
import { computed, inject, nextTick, onMounted, ref, watch } from "vue";
import InlineSvg from "vue-inline-svg";
import {
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  ShowLoadingImage,
} from "../../types";
import { useServiceStore } from "@/stores/serviceStore";
import deviceHelper from "@/utils/deviceInfo";
import RadiusSlider2 from "@/components/map/RadiusSlider2.vue";
import PageModal from "@/components/shared/modal/base/PageModal.vue";
import { useRoute } from "vue-router";
import MapOfNearbyBusStopsBottomSheet from "./MapOfNearbyBusStopsBottomSheet.vue";


const __default__ = {
  name: "MapOfNearbyBusStops",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  alignTitleToLeft: {
    type: Boolean,
    default: false,
  },
  targetBusStopId: {
    type: String,
    default: null,
  },
},
  setup(__props, { expose: __expose }) {
  __expose();

SwiperCore.use([EffectCoverflow, Navigation, Pagination]);



const getCurrentLocation = inject(
  PROVIDE_KEY_GET_CURRENT_LOCATION
) as GetCurrentLocation;
const currentLocation = inject(PROVIDE_KEY_CURRENT_LOCATION) as Ref<
  Location | undefined
>;
const isLocationPermissionDenied = inject(
  PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED
) as Ref<boolean>;

const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const route = useRoute();
const serviceStore = useServiceStore();

const isAndroid = deviceHelper.isAndroid();
const windowHeight = window.innerHeight;

// NavigationBar 아래 10px 위치로 maxHeight 계산
const maxHeightPx = computed(() => {
  const statusBarHeight = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue(
      "--statusbar-height"
    ) || "0"
  );
  const navHeight = 48; // $navHeight 값
  return windowHeight - (statusBarHeight + navHeight + 10);
});

const naverMap = ref<InstanceType<typeof NaverMap>>();
const bottomSheetModal =
  ref<InstanceType<typeof MapOfNearbyBusStopsBottomSheet>>();

const updateKey = ref(0);
const gpsButtonStyle = ref();

// 현재 위치
const currentLat = ref();
const currentLng = ref();

// 노선 목록 표시 여부
const lineInfoListVisibility = ref(false);
const updateShowLineInfoListVisibility = (show: boolean) => {
  lineInfoListVisibility.value = show;
};

// 주소 검색하기
const searchKeyword = ref("");
const updateSearchKeyword = (keyword: string) => {
  searchKeyword.value = keyword;
};

// 검색 결과 상태 관리
const hasSearchResults = ref(false);

// 검색 결과 상태 업데이트 함수
const updateSearchResultsStatus = (hasResults: boolean) => {
  hasSearchResults.value = hasResults;
};

// 선택한 주소 처리
const addressLatitude = ref<number | undefined>(currentLat.value);
const addressLongitude = ref<number | undefined>(currentLng.value);
const updateSelectedAddress = (address: IAddressSearchInfo) => {
  searchKeyword.value = address.roadAddress ?? address.dongAddress;
  if (
    addressLatitude.value === address.latitude &&
    addressLongitude.value === address.longitude
  ) {
    moveMapToCenter(addressLatitude.value, addressLongitude.value);
    updateSelectedBusStop(selectedBusStop.value);
    return;
  }

  addressLatitude.value = address.latitude;
  addressLongitude.value = address.longitude;
  updateMapCenter(address.latitude, address.longitude);
  // 주소 검색으로 인한 이동은 즉시 정류장 업데이트
  if (address.latitude && address.longitude) {
    forceUpdateBusStops(address.latitude, address.longitude);
  }
};

const lineTypeCodeCheckedMap = ref<ILineTypeCheckedMap>();
const updateCheckedLineTypesCodes = (checkedMap: {
  [lineTypeCode: string]: boolean;
}) => {
  lineTypeCodeCheckedMap.value = checkedMap;
};

const infoWindowContent = computed(
  () => `<div class="info-wrapper"><p>${searchKeyword.value}</p></div>`
);

const moveMapToCenter = (latitude?: number, longitude?: number) => {
  if (!latitude || !longitude) return;
  naverMap.value?.setCenter(latitude, longitude);
  updateMapCenter(latitude, longitude);
  // GPS 버튼으로 인한 이동은 즉시 정류장 업데이트
  forceUpdateBusStops(latitude, longitude);
};

const lineStore = useLineStore();
const nearBusStops = ref<IBusStopData[]>([]);

const isWithinRadius = (busStop: IBusStopData): boolean => {
  if (!naverMap.value) return false;

  return (
    getDistanceMeter(
      busStop,
      mapCenter.value?.latitude,
      mapCenter.value?.longitude
    ) <= radiusMeter.value
  );
};

const filteredBusStops = computed(() => {
  if (!naverMap.value || !nearBusStops.value.length) return [];

  const busStopsWithinRadius = nearBusStops.value
    .filter(isWithinRadius)
    .sort((a, b) => a.id.localeCompare(b.id));

  if (!lineTypeCodeCheckedMap.value) {
    return busStopsWithinRadius;
  }
  // 체크된 노선 타입 코드
  const checkedCodes = Object.entries(lineTypeCodeCheckedMap.value)
    .filter(([lineTypeCode, isChecked]) => isChecked)
    .map(([lineTypeCode]) => lineTypeCode);

  // 각 정류장의 노선 정보 중 체크된 노선 타입이 존재하는지 확인
  return busStopsWithinRadius.filter((busStop) => {
    const infos =
      lineStore.busStopIdToLineInfos.get(busStop.id) ||
      lineStore.getLineInfosLiteByBusStopId(busStop.id);
    return infos.some((info) => checkedCodes.includes(info.lineTypeCode));
  });
});

const lineInfos = computed<ILineInfoLite[]>(() => {
  if (!selectedBusStop.value) {
    return [];
  }
  return lineStore.getLineInfosLiteByBusStopId(selectedBusStop.value.id);
});

const selectedBusStop = ref<IBusStopData | undefined>();

const updateSelectedBusStop = (
  busStop?: IBusStopData,
  shouldUpdateMapCenter = true
) => {
  selectedBusStop.value = busStop;
  nextTick(() => {
    naverMap.value?.updateBusStopMarkerWithCenter(shouldUpdateMapCenter);
  });
};

const updateSelectedBusStopFromSlider = (busStop?: IBusStopData) => {
  // 슬라이더에서 호출될 때는 지도 중심 이동하지 않음 (무한 반복 방지)
  selectedBusStop.value = busStop;
  nextTick(() => {
    naverMap.value?.updateBusStopMarkerWithCenter(false);
  });
};

const showBusStopInfo = (busStop: IBusStopData) => {
  const index = filteredBusStops.value.indexOf(busStop);
  bottomSheetModal.value?.swiperInstance?.slideToLoop(index, 300, true);
  // 마커 클릭 시에는 지도 중심 이동하지 않음 (무한 반복 방지)
  updateSelectedBusStop(busStop, false);
};

const getDistanceMeter = (
  busStop: IBusStopData,
  lat: number,
  lng: number
): number => {
  return (
    calculateDistance(busStop.latitude, busStop.longitude, lat, lng) * 1000
  );
};

const movePreviousBusStop = () => {
  bottomSheetModal.value?.movePreviousBusStop();
};

const moveNextBusStop = () => {
  bottomSheetModal.value?.moveNextBusStop();
};

const mapCenter = ref<ICoordinateInfo>({
  latitude: currentLat.value,
  longitude: currentLng.value,
});

const updateMapCenter = (latitude?: number, longitude?: number) => {
  if (!naverMap.value) return;

  if (latitude && longitude) {
    mapCenter.value = { latitude, longitude };
  } else {
    mapCenter.value = naverMap.value.getCenter();
  }
};

const changeGpsButtonOpacity = (translucent: boolean) => {
  // gpsButtonStyle.value = {
  //   opacity: translucent ? 0.5 : 1,
  // };
};

const handleMapCenterChanged = (isSameAsInitial: boolean) => {
  // 기존 GPS 버튼 투명도 변경 기능만 유지
  changeGpsButtonOpacity(!isSameAsInitial);
};

const handleMapIdle = () => {
  // 지도가 완전히 정지했을 때 mapCenter 업데이트
  updateMapCenter();
  // 필요시 버스 정류장 데이터도 업데이트
  checkAndUpdateBusStopsIfNeeded();
};

// 드래그 종료시: 업데이트 필요 플래그만 설정
const markUpdateNeeded = () => {
  needsBusStopUpdate.value = true;
};

// idle시: 조건부로 버스 정류장 데이터 업데이트
const checkAndUpdateBusStopsIfNeeded = () => {
  if (!needsBusStopUpdate.value) return;

  const currentCenter = naverMap.value?.getCenter();
  if (
    currentCenter &&
    shouldSearchNewArea(currentCenter.latitude, currentCenter.longitude)
  ) {
    getNearBusStops(currentCenter.latitude, currentCenter.longitude, true);
    lastSearchCenter.value = {
      lat: currentCenter.latitude,
      lng: currentCenter.longitude,
    };
  }

  needsBusStopUpdate.value = false;
};

// 즉시 호출이 필요한 케이스들 (GPS, 주소검색, 초기로드)
const forceUpdateBusStops = (lat: number, lng: number) => {
  needsBusStopUpdate.value = false; // 플래그 리셋
  getNearBusStops(lat, lng);
  lastSearchCenter.value = { lat, lng };
};

// 정류장 반경 업데이트
const radiusMeterList = [500, 1000, 2000, 3000, 4000];
const radiusMeter = ref(radiusMeterList[0]);
const updateRadiusMeter = (meter: number) => {
  radiusMeter.value = meter;
};

// API 호출 최적화를 위한 상태 관리
const lastSearchCenter = ref<{ lat: number; lng: number } | null>(null);
const needsBusStopUpdate = ref(false);
const SEARCH_THRESHOLD_KM = 7.5; // 7.5km 이상 이동시에만 검색

const shouldSearchNewArea = (
  currentLat: number,
  currentLng: number
): boolean => {
  if (!lastSearchCenter.value) return true;

  const distance = calculateDistance(
    lastSearchCenter.value.lat,
    lastSearchCenter.value.lng,
    currentLat,
    currentLng
  );

  return distance > SEARCH_THRESHOLD_KM;
};

const shouldUpdateMapCenter = ref(false);

const getNearBusStops = async (
  lat?: number,
  lng?: number,
  keepCenter = false
) => {
  shouldUpdateMapCenter.value = !keepCenter;

  const nearBusStopsResult = await lineStore.getNearBusStops(
    lat ? lat : currentLat.value,
    lng ? lng : currentLng.value,
    BUS_STOP_MARKERS_WINDOW_SIZE_METER
  );

  if (!nearBusStopsResult.isSuccess) {
    showToastMessage("정류장을 가져오는데 실패하였습니다", 3500, 0);
    return;
  }

  nearBusStops.value = lineStore.nearBusStops;

  if (nearBusStops.value.length > 1) return;
  if (nearBusStops.value.length === 0) {
    handleNotFoundNearBusStops();
  }
};

const handleNotFoundNearBusStops = async () => {
  showToastMessage(
    `${radiusMeter.value / 1000}km 내 정류장이 없습니다`,
    3500,
    0
  );
};

const shouldShowStreetView = ref(false);
const toggleShowStreetView = (show: boolean) => {
  if (show && !selectedBusStop.value) return;

  shouldShowStreetView.value = show;
};

/** 지도 Zoom Level */
const MIN_ZOOM_LEVEL = 11;
const MAX_ZOOM_LEVEL = 18;

const zoomLevel = ref(15);
const updateZoomLevel = (level: number) => {
  if (level < MIN_ZOOM_LEVEL || level > MAX_ZOOM_LEVEL) return;

  zoomLevel.value = level;
};

/** 위치 권한 요청 팝업 */
const locationSettingGuide = computed(() => {
  const serviceName = serviceStore.serviceInfo?.name ?? "해피로드";
  return isAndroid
    ? `[설정 > 애플리케이션 > ${serviceName} > 권한 > 위치]`
    : `[설정 > 애플리케이션 > ${serviceName} > 위치]`;
});

const showLocationPermissionConfirmModal = () => {
  showErrorModal(
    "현재 위치 확인 불가",
    `현재 위치를 확인할 수 없습니다.<br />위치 권한이 허용되었는지 확인해주세요.<br />${locationSettingGuide.value}`,
    undefined,
    undefined,
    goToBack
  );
};

onMounted(async () => {
  showLoadingImage();
  getCurrentLocation();
});

const goToBack = () => {
  router.currentRoute.value.meta.willGoBack = true;
  router.back();
};

const updateLocationStates = (latitude: number, longitude: number) => {
  currentLat.value = latitude;
  currentLng.value = longitude;
  addressLatitude.value = latitude;
  addressLongitude.value = longitude;
  mapCenter.value = { latitude, longitude };
};

/** 현재 위치 가져온 후 처리 */
watch(
  () => currentLocation.value,
  async (currentLocation) => {
    if (route.name !== RouterName.MapOfNearbyBusStops) return;

    removeLoadingImage();

    if (
      !currentLocation ||
      currentLocation.latitude === -1 ||
      currentLocation.longitude === -1
    ) {
      showLocationPermissionConfirmModal();
      return;
    }

    const { latitude, longitude } = currentLocation;
    updateLocationStates(latitude, longitude);

    // 초기 로드는 즉시 정류장 업데이트
    forceUpdateBusStops(latitude, longitude);
    bottomSheetModal.value?.swiperInstance?.slideToLoop(0);
  }
);

/** 위치 권한 거부 여부 */
watch(
  () => isLocationPermissionDenied.value,
  (isLocationPermissionDenied) => {
    removeLoadingImage();

    if (isLocationPermissionDenied) {
      showLocationPermissionConfirmModal();
    }
  }
);

watch(
  () => filteredBusStops.value,
  (newBusStops, oldBusStops) => {
    const isRemoved = newBusStops.length < oldBusStops.length;
    // 추가, 삭제, 옵션이 변경된 정류장
    const changedBusStops = _.differenceBy(
      isRemoved ? oldBusStops : newBusStops,
      isRemoved ? newBusStops : oldBusStops,
      "id"
    );
    if (!changedBusStops.length) return;

    updateKey.value += 1;

    // 기존 선택된 정류장이 새 목록에도 있으면 유지
    const keepSelected =
      selectedBusStop.value &&
      newBusStops.some((stop) => stop.id === selectedBusStop.value!.id);

    if (!keepSelected) {
      // 자동 선택 시에는 지도 중심을 이동하지 않음 (무한 반복 방지)
      updateSelectedBusStop(
        filteredBusStops.value?.[0],
        false // shouldUpdateMapCenter를 false로 설정
      );
    }
  }
);

const __returned__ = { getCurrentLocation, currentLocation, isLocationPermissionDenied, showLoadingImage, removeLoadingImage, showErrorModal, showToastMessage, route, serviceStore, isAndroid, windowHeight, maxHeightPx, naverMap, bottomSheetModal, updateKey, gpsButtonStyle, currentLat, currentLng, lineInfoListVisibility, updateShowLineInfoListVisibility, searchKeyword, updateSearchKeyword, hasSearchResults, updateSearchResultsStatus, addressLatitude, addressLongitude, updateSelectedAddress, lineTypeCodeCheckedMap, updateCheckedLineTypesCodes, infoWindowContent, moveMapToCenter, lineStore, nearBusStops, isWithinRadius, filteredBusStops, lineInfos, selectedBusStop, updateSelectedBusStop, updateSelectedBusStopFromSlider, showBusStopInfo, getDistanceMeter, movePreviousBusStop, moveNextBusStop, mapCenter, updateMapCenter, changeGpsButtonOpacity, handleMapCenterChanged, handleMapIdle, markUpdateNeeded, checkAndUpdateBusStopsIfNeeded, forceUpdateBusStops, radiusMeterList, radiusMeter, updateRadiusMeter, lastSearchCenter, needsBusStopUpdate, SEARCH_THRESHOLD_KM, shouldSearchNewArea, shouldUpdateMapCenter, getNearBusStops, handleNotFoundNearBusStops, shouldShowStreetView, toggleShowStreetView, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL, zoomLevel, updateZoomLevel, locationSettingGuide, showLocationPermissionConfirmModal, goToBack, updateLocationStates, AddressSearch, BusStopStreetView, MapLineTypeFilter, MapZoomLevelSetter, NaverMap, NavigationBar, Page, get InlineSvg() { return InlineSvg }, RadiusSlider2, PageModal, MapOfNearbyBusStopsBottomSheet }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})